import { c as _c } from "react/compiler-runtime";
import { IconAlertCircle, IconRosetteDiscountCheck, IconSchool, IconSpy } from "@tabler/icons-react";
import { KeyValue } from "@/components/domain/key-value";
import { KeyValueGrid } from "@/components/domain/key-value-grid";
import { RegistrationRawDataDialog } from "@/components/domain/registration/registration-raw-data-dialog";
import { RelativeAgeString } from "@/components/domain/relative-age";
import { RelativeExpiryString } from "@/components/domain/relative-expiry";
import { ReportSection } from "@/components/domain/report-section";
import { ProviderLogo } from "@/components/icons/provider-logo";
import { sections } from "@/lib/constants/sections";
import { describeRegistrationSource } from "@/lib/registration-source";
import type { RegistrationResponse } from "@domainstack/types";
import { Alert, AlertDescription, AlertTitle } from "@domainstack/ui/alert";
import { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger } from "@domainstack/ui/responsive-tooltip";
import { formatDate, formatDateTimeUtc, toDateTimeAttr } from "@domainstack/utils/date";
import { describeRegistrant, type RegistrantView } from "@domainstack/utils/registrant";
function getUnavailableMessage(data: RegistrationResponse): string {
  if (data.unavailableReason === "timeout") {
    return "WHOIS/RDAP lookup timed out. This may be a temporary issue with the registry's servers.";
  }
  if (data.unavailableReason === "unsupported_tld") {
    return `The .${data.tld} registry does not publish public WHOIS/RDAP data. Registration details cannot be verified for this domain.`;
  }
  return "Registration information could not be retrieved at this time.";
}
function RegistrationUnavailableNotice(t0) {
  const $ = _c(6);
  const {
    data
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconAlertCircle aria-hidden="true" />;
    t2 = <AlertTitle>Registration Data Unavailable</AlertTitle>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== data) {
    t3 = getUnavailableMessage(data);
    $[2] = data;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Alert variant="warning">{t1}{t2}<AlertDescription>{t3}</AlertDescription></Alert>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function RegistrarVerifiedBy(t0) {
  const $ = _c(13);
  const {
    serverUrl,
    serverName,
    learnUrl,
    sourceLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ResponsiveTooltipTrigger nativeButton={false} render={<IconRosetteDiscountCheck className="!size-3.5 text-muted-foreground/80" aria-hidden="true" />} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== serverName || $[2] !== serverUrl) {
    t2 = serverUrl ? <a href={serverUrl} target="_blank" rel="noopener" className="underline underline-offset-2">{serverName}</a> : serverName;
    $[1] = serverName;
    $[2] = serverUrl;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span>Verified by{" "}<span className="font-medium">{t2}</span></span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `Learn about ${sourceLabel}`;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconSchool className="size-3" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== learnUrl || $[8] !== t4) {
    t6 = <a href={learnUrl} target="_blank" rel="noopener" title={t4} className="text-muted/80">{t5}</a>;
    $[7] = learnUrl;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <ResponsiveTooltip>{t1}<ResponsiveTooltipContent><div className="flex items-center gap-[5px]">{t3}{t6}</div></ResponsiveTooltipContent></ResponsiveTooltip>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
function RegistrationDetailsGrid(t0) {
  const $ = _c(40);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.contacts || $[1] !== data.privacyEnabled) {
    t1 = describeRegistrant(data.contacts, data.privacyEnabled);
    $[0] = data.contacts;
    $[1] = data.privacyEnabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const registrant = t1;
  const t2 = data.registrarProvider?.name || "Unknown";
  let t3;
  if ($[3] !== data.registrarProvider) {
    t3 = data.registrarProvider?.id ? <ProviderLogo providerId={data.registrarProvider.id} providerName={data.registrarProvider.name} initialUrl={data.registrarProvider.logoUrl} /> : undefined;
    $[3] = data.registrarProvider;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data) {
    t4 = describeRegistrationSource(data);
    $[5] = data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <RegistrarVerifiedBy {...t4} />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = <KeyValue label="Registrar" value={t2} leading={t3} suffix={t5} />;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== registrant) {
    t7 = <RegistrantKeyValue view={registrant} />;
    $[13] = registrant;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== data.creationDate) {
    t8 = data.creationDate ? <time dateTime={toDateTimeAttr(data.creationDate)} suppressHydrationWarning={true}>{formatDate(data.creationDate)}</time> : "Unknown";
    $[15] = data.creationDate;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== data.creationDate) {
    t9 = data.creationDate ? <time dateTime={toDateTimeAttr(data.creationDate)} suppressHydrationWarning={true}>{formatDateTimeUtc(data.creationDate)}</time> : undefined;
    $[17] = data.creationDate;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== data.creationDate) {
    t10 = data.creationDate ? <span className="text-[11px] leading-none text-muted-foreground"><RelativeAgeString from={data.creationDate} /></span> : null;
    $[19] = data.creationDate;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8 || $[23] !== t9) {
    t11 = <KeyValue label="Created" value={t8} valueTooltip={t9} suffix={t10} />;
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== data.expirationDate) {
    t12 = data.expirationDate ? <time dateTime={toDateTimeAttr(data.expirationDate)} suppressHydrationWarning={true}>{formatDate(data.expirationDate)}</time> : "Unknown";
    $[25] = data.expirationDate;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== data.expirationDate) {
    t13 = data.expirationDate ? <time dateTime={toDateTimeAttr(data.expirationDate)} suppressHydrationWarning={true}>{formatDateTimeUtc(data.expirationDate)}</time> : undefined;
    $[27] = data.expirationDate;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== data.expirationDate) {
    t14 = data.expirationDate ? <span className="text-[11px] leading-none text-muted-foreground"><RelativeExpiryString to={data.expirationDate} dangerDays={30} warnDays={45} /></span> : null;
    $[29] = data.expirationDate;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t14) {
    t15 = <KeyValue label="Expires" value={t12} valueTooltip={t13} suffix={t14} />;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15 || $[37] !== t6 || $[38] !== t7) {
    t16 = <KeyValueGrid colsDesktop={2}>{t6}{t7}{t11}{t15}</KeyValueGrid>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t6;
    $[38] = t7;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
export function RegistrationSection(t0) {
  const $ = _c(14);
  const {
    data
  } = t0;
  if (!data) {
    return null;
  }
  const isWhoisUnavailable = data.status === "unknown";
  let t1;
  if ($[0] !== data) {
    t1 = describeRegistrationSource(data);
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    serverUrl,
    serverName
  } = t1;
  let t2;
  if ($[2] !== data.domain || $[3] !== data.hasRawResponse || $[4] !== data.source || $[5] !== serverName || $[6] !== serverUrl) {
    t2 = data.hasRawResponse ? <RegistrationRawDataDialog domain={data.domain} format={data.source === "rdap" ? "RDAP" : "WHOIS"} serverName={serverName} serverUrl={serverUrl} /> : undefined;
    $[2] = data.domain;
    $[3] = data.hasRawResponse;
    $[4] = data.source;
    $[5] = serverName;
    $[6] = serverUrl;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== data || $[9] !== isWhoisUnavailable) {
    t3 = isWhoisUnavailable ? <RegistrationUnavailableNotice data={data} /> : <RegistrationDetailsGrid data={data} />;
    $[8] = data;
    $[9] = isWhoisUnavailable;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <ReportSection {...sections.registration} headerActions={t2}>{t3}</ReportSection>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function RegistrantKeyValue(t0) {
  const $ = _c(10);
  const {
    view
  } = t0;
  const redacted = view?.state === "redacted";
  const named = view?.state === "named";
  const primary = redacted ? "Hidden" : (view?.name ?? (view?.state === "location-only" ? view.location : undefined)) || "Not published";
  const t1 = redacted ? "Registrant details are redacted by the registry or registrar" : undefined;
  let t2;
  if ($[0] !== redacted) {
    t2 = redacted ? <IconSpy className="text-muted-foreground" aria-hidden="true" /> : undefined;
    $[0] = redacted;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== named || $[3] !== view) {
    t3 = named && view?.location ? <span className="truncate text-[11px] leading-none text-muted-foreground">{view.location}</span> : null;
    $[2] = named;
    $[3] = view;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== primary || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <KeyValue label="Registrant" value={primary} valueTooltip={t1} leading={t2} suffix={t3} />;
    $[5] = primary;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}